// ── @matix/utils/money ────────────────────────────────────────────────────
//
// The single money-rounding rule for the frontends. It must stay byte-for-byte
// equivalent to pos-api's `src/utils/money.ts` — the cart, the wire builder and
// the server all round the same way or the till collects an amount the server
// rejects (PAYMENT_SUM_MISMATCH).

/** Currencies never carry more than this many decimals in Matix. */
const MAX_PRECISION = 8;
/** Used when the tenant's currency hasn't resolved yet (EGP/SAR/AED/USD). */
export const DEFAULT_MONEY_PRECISION = 2;

/**
 * Clamp a currency precision (`mstr_Currencies.CurrencyPrecision`) to something
 * safe to feed `Math.round`. A missing/garbage value degrades to 2 decimals.
 */
export function safePrecision(precision: number | null | undefined): number {
  if (typeof precision !== 'number' || !Number.isFinite(precision)) {
    return DEFAULT_MONEY_PRECISION;
  }
  return Math.max(0, Math.min(MAX_PRECISION, Math.trunc(precision)));
}

/**
 * Round money at the TENANT's currency precision — 3 decimals for KWD/BHD/OMR,
 * 2 for EGP/SAR/AED, 0 for JPY-style currencies.
 *
 * Why this is parameterized rather than a fixed 2: a KWD line of 1 × 0.136
 * snapped to 2 decimals becomes 0.14, and the receipt (which formats at the
 * currency's 3 decimals) then printed "0.140" for an item priced 0.136. Over a
 * basket the fabricated fils add up and the printed lines stop summing to the
 * printed total.
 *
 * Rounds HALF AWAY FROM ZERO on the magnitude, then reapplies the sign, so a
 * negative return total ending in .xx5 rounds to the same magnitude as its
 * positive twin. Plain `Math.round` rounds half toward +∞ and would split the
 * smallest unit between the sale and return legs of an exchange.
 */
export function roundMoney(n: number, precision: number | null | undefined): number {
  if (!Number.isFinite(n)) return 0;
  const factor = 10 ** safePrecision(precision);
  return (Math.sign(n) * Math.round(Math.abs(n) * factor)) / factor;
}

/**
 * Sum money the round-then-sum way: round EACH term at the currency precision
 * before adding, then round the sum. This mirrors what the receipt prints
 * (per-line totals) so the printed lines always add up to the printed total.
 */
export function sumMoney(values: number[], precision: number | null | undefined): number {
  return roundMoney(
    values.reduce((acc, v) => acc + roundMoney(v, precision), 0),
    precision,
  );
}
